'use client';

import React, { useState } from 'react';
import Button from '@/ui/components/shared/Button';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import { FRIENDLY_CAPTCHA_ENDPOINT } from '@/integrations/captcha/constants';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { PersonioApiOpenPosition, PersonioApplicationFileCategory } from '@/types/career-types';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import PrivacyPolicyCheckBox from '@/ui/components/shared/PrivacyPolicyCheckbox/PrivacyPolicyCheckbox';
import RemoveIcon from '../../../../../../public/images/remove-icon.svg';
import UploadIcon from '../../../../../../public/images/upload-icon.svg';
import ThankYouForApplication from './thank-you';

interface AttachmentWithCategory {
  file: File;
  category: string;
}

interface JobApplicationFormProps {
  job?: PersonioApiOpenPosition;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_TOTAL_SIZE = 64 * 1024 * 1024;

export function JobApplicationForm({ job }: JobApplicationFormProps) {
  const { language } = useLocalization();
  const t = useTranslations('JobApplicationForm');

  const [attachments, setAttachments] = useState<AttachmentWithCategory[]>([]);
  const [isSubmissionFailed, setIsSubmissionFailed] = useState(false);
  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);

  const formSchema = yup.object({
    firstName: yup
      .string()
      .required(t('First name is required'))
      .max(50, t('Maximum 50 characters allowed')),
    lastName: yup
      .string()
      .required(t('Last name is required'))
      .max(50, t('Maximum 50 characters allowed')),
    email: yup.string().email(t('Invalid email')).required(t('Email is required')),
    phone: yup.string().required(t('Phone number is required')),
    dateOfBirth: yup.string().required(t('Date of birth is required')),
    salaryExpectation: yup.string().required(t('Salary expectations are required')),
    availableFrom: yup.string().required(t('Available from is required')),
    linkedInUrl: yup.string(),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('You must accept the privacy policy'))
      .required(t('You must accept the privacy policy')),
  });

  // Custom validation to ensure either LinkedIn URL or documents are provided
  const validationSchema = formSchema
    .test({
      name: 'linkedInOrDocuments',
      message: t('Either LinkedIn URL or documents must be provided'),
      test: (values) => {
        if (!values) {
          return false;
        }

        return !!values?.linkedInUrl || attachments.length > 0;
      },
    })
    .test({
      name: 'attachmentsFileSize',
      message: t('Maximum file size is 20MB'),
      test: () => attachments.every((attachment) => attachment.file.size <= MAX_FILE_SIZE),
    })
    .test({
      name: 'attachmentsTotalSize',
      message: t('Maximum upload size is 64MB'),
      test: () => {
        const totalSize = attachments.reduce((acc, attachment) => acc + attachment.file.size, 0);
        return totalSize <= MAX_TOTAL_SIZE;
      },
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<yup.InferType<typeof validationSchema>>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      salaryExpectation: '',
      availableFrom: '',
      linkedInUrl: '',
      privacyPolicy: false,
    },
  });

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'auto',
    language: language as any,
    showAttribution: true,
  });

  const onSubmit = async (data: yup.InferType<typeof validationSchema>) => {
    setIsSubmissionFailed(false);

    try {
      const formData = new FormData();

      formData.append('jobPositionId', job!.id.toString());
      formData.append('recruitingChannelId', '');
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      formData.append('email', data.email);
      formData.append('phone', data.phone);
      formData.append('dateOfBirth', data.dateOfBirth);
      formData.append('salaryExpectation', data.salaryExpectation);
      formData.append('availableFrom', data.availableFrom);
      formData.append('linkedInUrl', data.linkedInUrl || '');
      formData.append('frcCaptchaSolution', captchaHook.captchaStatus.solution as string);

      attachments.forEach((attachment, index) => {
        formData.append(`attachments[${index}][file]`, attachment.file);
        formData.append(
          `attachments[${index}][category]`,
          attachment?.category || PersonioApplicationFileCategory.Other,
        );
      });

      // Send POST request
      const response = await fetch('/api/career/jobs/apply', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      setIsSubmissionSuccess(true);
      reset();
    } catch (error) {
      captchaHook.resetWidget();
      setIsSubmissionFailed(true);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    const newFiles = droppedFiles.map((file) => ({
      file,
      category: '',
    }));
    setAttachments((prev) => [...prev, ...newFiles]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const newFiles = selectedFiles.map((file) => ({
        file,
        category: '',
      }));
      setAttachments((prev) => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  if (!job) {
    return (
      <div className="flex justify-center">
        <h1>{t('Job not found')}</h1>
      </div>
    );
  }

  if (isSubmissionSuccess) {
    return <ThankYouForApplication />;
  }

  return (
    <div className="flex justify-center">
      <form
        className="grid max-w-[56rem] grid-cols-1 gap-y-6"
        id={`applicationForm-${job.id}`}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="mb-2 sm:mb-8">
          <h1 className="text-[1.625em] leading-[1.2] sm:text-[3em]">
            <span>{t('Apply now as')}</span>
            <br />
            <span className="font-bold">{job.name}</span>
          </h1>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 md:gap-8">
          <div>
            <TextInput
              id="firstName"
              register={register}
              errors={errors.firstName}
              label={t('First Name')}
              placeholder={t('First Name')}
              required
            />
          </div>
          <div>
            <TextInput
              id="lastName"
              register={register}
              errors={errors.lastName}
              label={t('Last Name')}
              placeholder={t('Last Name')}
              required
            />
          </div>
        </div>

        <div>
          <TextInput
            id="email"
            register={register}
            errors={errors.email}
            label={t('Email')}
            placeholder={t('Email address')}
            required
            type="email"
          />
        </div>

        <div>
          <TextInput
            id="phone"
            register={register}
            errors={errors.phone}
            label={t('Phone number')}
            placeholder="+43 676 12452348"
            required
          />
        </div>

        <div>
          <TextInput
            id="dateOfBirth"
            register={register}
            errors={errors.dateOfBirth}
            label={t('Date of birth')}
            placeholder="DD.MM.YYYY"
            required
            type="date"
          />
        </div>

        <div>
          <TextInput
            id="salaryExpectation"
            register={register}
            errors={errors.salaryExpectation}
            label={t('Salary expectations')}
            placeholder={t('How much do you want')}
            required
          />
        </div>

        <div>
          <p className="block text-[1rem] font-bold text-gray-900">
            {t('Documents or LinkedIn')} <span className="text-primary-100">*</span>
          </p>
          <p className="mb-2 text-[1rem] text-gray-500">
            {t(
              'Please upload your CV, current certificates and a short cover letter (up to 20 MB in total) or apply quickly and easily using your LinkedIn profile',
            )}
          </p>
          <TextInput
            id="linkedInUrl"
            register={register}
            errors={errors.linkedInUrl}
            placeholder={t('LinkedIn profile URL')}
            required
          />

          {['linkedInOrDocuments', 'attachmentsFileSize', 'attachmentsTotalSize'].includes(
            (errors as any)['']?.type,
          ) && <p className="mt-1 text-sm text-red">{(errors as any)['']?.message}</p>}

          <div
            onDrop={handleFileDrop}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add('bg-gray-100');
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove('bg-gray-100');
            }}
            className="relative mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center sm:mb-8"
          >
            <div className="flex justify-center">
              <Image src={UploadIcon} alt="" width={32} height={32} />
            </div>
            <div className="mb-4">{t('Drag & Drop')}</div>
            <label
              htmlFor="attachments"
              className="mb-4 cursor-pointer text-[1rem] text-gray-500 before:absolute before:inset-0"
            >
              {t('your files here or click to browse')}
              <br />
              {t('(JPEG, PNG, PDF with max size 20 MB)')}
            </label>
            <input
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              id="attachments"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </div>
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="mt-2 flex items-center justify-between rounded border-b border-gray-100"
            >
              <div className="space-y-1 pb-2">
                <span className="block text-[1rem] font-bold">{attachment.file.name}</span>

                <div className="relative w-max">
                  <select
                    value={attachment.category}
                    onChange={(e) => {
                      const newAttachments = [...attachments];
                      newAttachments[index].category = e.target.value;
                      setAttachments(newAttachments);
                    }}
                    className="cursor-pointer appearance-none bg-white pr-6"
                  >
                    <option value="">{t('Select category')}</option>
                    <option value="cv">{t('CV')}</option>
                    <option value="cover-letter">{t('Cover letter')}</option>
                    <option value="certificate">{t('Certificate')}</option>
                    <option value="other">{t('Other')}</option>
                  </select>

                  {/* Dropdown Icon */}
                  <div className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2">
                    <svg
                      className="h-4 w-4 text-black"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => removeFile(index)}>
                <Image
                  src={RemoveIcon}
                  alt="Remove"
                  width={32}
                  height={32}
                  className="text-red-500 h-5 w-5"
                />
              </button>
            </div>
          ))}
        </div>

        <div>
          <TextInput
            id="availableFrom"
            register={register}
            errors={errors.availableFrom}
            label={t('Available from')}
            placeholder={t('Your availability')}
            required
          />
        </div>

        <div className="space-y-4">
          <PrivacyPolicyCheckBox
            register={register}
            errors={errors.privacyPolicy}
            internalPagesLinksKey="jobApplicationPrivacyPolicy"
          />
        </div>

        <div>
          <div className="inline-block min-w-min">
            {captchaHook.CaptchaWidget({ className: 'relative' })}
          </div>
        </div>

        <div>
          {isSubmissionFailed && (
            <p className="text-red">
              {t.rich('errorJobApplicationSubmission', {
                // eslint-disable-next-line react/no-unstable-nested-components
                link: (chunks) => (
                  <a href="mailto:career@nuki.io" className="underline">
                    {chunks}
                  </a>
                ),
              })}
            </p>
          )}
        </div>

        <div>
          <Button
            type="submit"
            className="mt-4 py-2"
            id={`submitApplication-${job.id}`}
            disabled={isSubmitting}
          >
            {t('Submit application')}
          </Button>
        </div>
      </form>
    </div>
  );
}
